import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import WebsiteLayout from "./layouts/WebsiteLayout";
import DashboardLayout from "./layouts/DashboardLayout";
import Home from "./pages/Home";
import Dashboard from "./pages/dashboard/Dashboard";
import ProductandInventory from "./pages/dashboard/ProductandInventory";
import ProductForm from "./pages/dashboard/ProductForm";
import CategoryManagement from "./pages/dashboard/CategoryManagement";
import PartBookAdmin from "./pages/dashboard/PartBookAdmin";
import CmsPages from "./pages/dashboard/CmsPages";
import Order from "./pages/dashboard/Order";
import OrderDetail from "./pages/dashboard/OrderDetail";
import Return from "./pages/dashboard/Return";
import Channelsyn from "./pages/dashboard/Channelsyn";
import Warehouse from "./pages/dashboard/Warehouse";
import Tax from "./pages/dashboard/Tax";
import Customorder from "./pages/dashboard/Customorder";
import Customers from "./pages/dashboard/Customers";
import Reviews from "./pages/dashboard/Reviews";
import Setting from "./pages/dashboard/Setting";
import Newsletter from "./pages/dashboard/Newsletter";
import NotificationsCenter from "./pages/dashboard/NotificationsCenter";
import ActivityLogs from "./pages/dashboard/ActivityLogs";
import HelpSupport from "./pages/dashboard/HelpSupport";
import Automation from "./pages/dashboard/Automation";
import MediaLibrary from "./pages/dashboard/MediaLibrary";
import Contact from "./pages/Contact";
import Support from "./pages/Support";
import AllProducts from "./pages/AllProducts";
import CategoriesPage from "./pages/CategoriesPage";
import Account from "./pages/Account";
import ResetPassword from "./pages/ResetPassword";
import UserAccount from "./pages/UserAccount";
import InvoicePage from "./pages/InvoicePage";
import PolicyPage from "./pages/PolicyPage";
import AboutUs from "./pages/AboutUs";
import Productdetails from "./pages/Productdetails";
import PartBooks from "./pages/PartBooks";
import {
  CartPage,
  CheckoutPage,
  PaymentDetailsPage,
  ThankYouPage,
  TrackOrderPage,
} from "./pages/EcommerceFlow";
import { scrollPageToTop } from "./utils/scrollToTop";

import "antd/dist/reset.css";
import "./layouts/css/custom.css";

function ScrollToTop() {
  const { hash, pathname, search } = useLocation();

  useEffect(() => {
    if (hash) return;

    window.requestAnimationFrame(() => scrollPageToTop());
  }, [hash, pathname, search]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route element={<WebsiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/support" element={<Support />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/all-products" element={<AllProducts />} />
            <Route path="/categories" element={<CategoriesPage />} />
            <Route path="/account" element={<Account />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route element={<ProtectedRoute roles={["user", "admin"]} />}>
              <Route path="/my-account" element={<UserAccount />} />
            </Route>
            <Route element={<ProtectedRoute roles={["admin"]} />}>
              <Route path="/admin-preview/products/:productId" element={<Productdetails adminPreview />} />
            </Route>
            <Route path="/terms-and-conditions" element={<PolicyPage type="terms" />} />
            <Route path="/privacy-policy" element={<PolicyPage type="privacy" />} />
            <Route path="/refund-policy" element={<PolicyPage type="refund" />} />
            <Route path="/part-book" element={<PartBooks />} />
            <Route path="/shop" element={<Navigate to="/all-products" replace />} />
            <Route path="/Productdetails" element={<Productdetails />} />
            <Route path="/products/:productKey" element={<Productdetails />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/payment-details" element={<PaymentDetailsPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/track-order" element={<TrackOrderPage />} />
            <Route path="/invoice/:token" element={<InvoicePage />} />
          </Route>
          <Route element={<ProtectedRoute roles={["admin"]} />}>
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="products" element={<ProductandInventory />} />
              <Route path="products/new" element={<ProductForm />} />
              <Route path="products/:productId/edit" element={<ProductForm />} />
              <Route path="categories" element={<CategoryManagement />} />
              <Route path="orders" element={<Order />} />
              <Route path="orders/:orderId" element={<OrderDetail />} />
              <Route path="shipping" element={<Navigate to="/dashboard/orders" replace />} />
              <Route path="returns" element={<Return />} />
              <Route path="sync" element={<Channelsyn />} />
              <Route path="warehouse" element={<Warehouse />} />
              <Route path="reports" element={<Tax />} />
              <Route path="part-books" element={<PartBookAdmin />} />
              <Route path="cms" element={<CmsPages />} />
              <Route path="media" element={<MediaLibrary />} />
              <Route path="custom-order" element={<Customorder />} />
              <Route path="parts" element={<Navigate to="/dashboard/custom-order" replace />} />
              <Route path="customers" element={<Customers />} />
              <Route path="reviews" element={<Reviews />} />
              <Route path="notifications" element={<NotificationsCenter />} />
              <Route path="newsletter" element={<Newsletter />} />
              <Route path="activity" element={<ActivityLogs />} />
              <Route path="support" element={<HelpSupport />} />
              <Route path="automation" element={<Automation />} />
              <Route path="settings" element={<Setting />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
